import { forEach } from '@angular/router/src/utils/collection';
import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  EventEmitter,
  Output,
} from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import * as moment from 'moment';
import {
  FieldMapItem,
  NotificationService,
  TableSettings,
  TableModeEnum,
  TableActionEnum,
  ModalService,
} from 'beyond-util';

import { BackendService } from '../../../services/backend.service';
import { UredjivanjeActionTypeEnum } from '../../../enums/uredjivanje-action-type.enum';
import { FormCode } from '../../../enums/form-code.enum';
import { FormName } from '../../../enums/form-name.enum';
import { EndpointTypes } from '../../../enums/enpoint-types.enum';
import { MontazaService } from '../../../services/montaza.service';
import {
  MONTAZA_DESTINACIJA,
  MONTAZA_KOPIRAJ_EMISIJU,
  MONTAZA_PREMESTI_EMISIJU,
  MONTAZA_KOPIRAJ_REZERVACIJU,
  MONTAZA_PREMESTI_REZERVACIJU,
  MONTAZA_KOPIRAJ_SVE,
  MONTAZA_PREMESTI_SVE,
} from '../../../constants/tooltip-messages';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-uredjivanje-details',
  templateUrl: './uredjivanje-details.component.html',
})
export class UredjivanjeDetailsComponent implements OnInit, OnDestroy {
  @Input() programFieldSettings: FieldMapItem;
  @Input() vremeFieldSettings: FieldMapItem;
  @Input() datumFieldSettings: FieldMapItem;
  @Input() cenaFieldSettings: FieldMapItem;
  @Input() nazivFieldSettings: FieldMapItem;
  @Input() targetDatumFieldSettings: FieldMapItem;
  @Input() selectedProgram: any;
  @Input() programi: any[];

  @Output() sacuvaj: EventEmitter<any> = new EventEmitter();
  @Output() osveziMontazuPoslePromeneEmisije: EventEmitter<any> = new EventEmitter();
  @Output() fetchMontaza: EventEmitter<any> = new EventEmitter();

  private _subscriptions: any[] = [];

  public UredjivanjeActionTypeEnum = UredjivanjeActionTypeEnum;
  public TableModeEnum = TableModeEnum;

  public akcijaType = UredjivanjeActionTypeEnum.Emisije;
  public zapocetoUredjivanje = false;
  public selectedDatum: any;

  public selectedEmisija: any;
  public targetDatum: any;
  public targetProgram: any;
  public targetEmisija: any;

  private _selectedRezervacije: any[] = [];
  public selectedRezervacija: any;

  public destinacijaFilter = '';
  public emisije: any[] = [];

  private rezervacijeTableSettingsSource: Subject<any> = new Subject<any>();
  public rezervacijeTableSettings$ = this.rezervacijeTableSettingsSource.asObservable();

  private rezervacijeSource: Subject<any> = new Subject<any>();
  public rezervacije$ = this.rezervacijeSource.asObservable();

  private emisijeTableSettingsSource: Subject<any> = new Subject<any>();
  public emisijeTableSettings$ = this.emisijeTableSettingsSource.asObservable();

  private emisijeSource: Subject<any> = new Subject<any>();
  public emisije$ = this.emisijeSource.asObservable();

  public tooltips = {
    MONTAZA_DESTINACIJA,
    MONTAZA_KOPIRAJ_SVE,
    MONTAZA_PREMESTI_SVE,
  };

  get actionTypeEmisije(): boolean {
    return this.akcijaType === UredjivanjeActionTypeEnum.Emisije;
  }

  get actionTypeRezervacije(): boolean {
    return this.akcijaType === UredjivanjeActionTypeEnum.Rezervacije;
  }

  get kopirajTooltipMessage(): string {
    return this.actionTypeRezervacije
      ? MONTAZA_KOPIRAJ_REZERVACIJU
      : MONTAZA_KOPIRAJ_EMISIJU;
  }

  get premestiTooltipMessage(): string {
    return this.actionTypeRezervacije
      ? MONTAZA_PREMESTI_REZERVACIJU
      : MONTAZA_PREMESTI_EMISIJU;
  }

  get tooltipOptions(): any {
    return {
      placement: 'top',
      'show-delay': '500',
      theme: 'light',
    };
  }

  get isEmisijaMontirana(): boolean {
    return this.selectedEmisija && this.selectedEmisija.Status === 2;
  }

  constructor(
    private _notificationService: NotificationService,
    private _backendService: BackendService,
    private _montazaService: MontazaService,
    private _modalService: ModalService,
    private _spinner: NgxSpinnerService
  ) {}

  ngOnInit() {
    this.fetchEmisijaTableSettings();
    this.subscribeToEmisijaData();
    this.subscribeToPremestiEmisiju();
    this.nazivFieldSettings.editable = true;
  }

  ngOnDestroy() {
    this._subscriptions.forEach((s: Subscription) => s.unsubscribe());
  }

  subscribeToEmisijaData() {
    this._subscriptions.push(
      this._montazaService.emisijaData$.subscribe(data => {
        this.selectedEmisija = data;
        const rezervacije = this._montazaService.montaza.RezervacijeNaMontazi.filter(
          s =>
            !s.Spica &&
            !s.JePlaceholder &&
            this.selectedEmisija.EmisijaRasporedId === s.EmisijaRasporedId
        );
        let count = 1;
        rezervacije.forEach(s => {
          s.UiRbr = count++;
          s.Sifra = s.SpotSifra;
          s.Naziv = s.SpotNaziv;
          s.TrajanjeSekunde = s.SpotTrajanje;
          s.Spica = s.JeSpica;
          s.RezervacijaId = s.Id;
          s.KlijentId = s.KupacId;
          s.KlijentNaziv = s.KupacNaziv;
        });

        this.selectedEmisija.Spotovi = rezervacije;
        this.zapocetoUredjivanje = true;
        this.fetchRezervacijeSettings();
      })
    );
  }

  subscribeToPremestiEmisiju() {
    this._subscriptions.push(
      this._montazaService.premestiEmisiju$.subscribe(() => {
        this.selectedEmisija = null;
        this.selectedRezervacija = null;
        this.zapocetoUredjivanje = false;
      })
    );
  }

  fetchRezervacijeSettings() {
    const payload = this._backendService.getRequestPayload(
      FormCode.MSMON_URE,
      FormName.Montaza,
      EndpointTypes.GetModelData,
      ''
    );

    this._subscriptions.push(
      this._backendService
        .fetchMediaPlanSettings(payload)
        .subscribe((response: any) => {
          const settings = response.Settings;
          const tableColumns = [
            'ZeljenaPozicija',
            'Sifra',
            'Naziv',
            'TrajanjeSekunde',
          ];
          const allTableFields = settings.allFields.filter(field => {
            return tableColumns.includes(field.name);
          });
          const numField = settings.allFields.find(
            field => field.name === 'UiRbr'
          );

          if (numField) {
            numField.width = 1;
            numField.sortable = false;
            allTableFields.unshift(numField);
          }

          settings.formSettings.table = allTableFields.map(field => field.id);

          const rezervacijeTableSettings = new TableSettings(settings, 'table');

          this.rezervacijeTableSettingsSource.next(rezervacijeTableSettings);
          this.rezervacijeSource.next(this.getAllRezervacije());
        })
    );
  }

  getAllRezervacije(kopiranje?: boolean) {
    if (kopiranje) {
      this.selectedEmisija.Spotovi.forEach(s => {
        s.KupacId = s.KlijentId;
        s.KupacNaziv = s.KlijentNaziv;
        s.SpotSifra = s.Sifra;
        s.SpotNaziv = s.Naziv;
        s.Trajanje = this.selectedEmisija.Trajanje;
        s.SpotTrajanje = s.TrajanjeSekunde;
        s.SpotTrajanjeEstimacija = s.SpotTrajanjeEstimacija;
      });
    }

    return this.selectedEmisija.Spotovi.filter(
      s => !s.Spica && !s.JePlaceholder
    );
  }

  rezervacijeTableAction(event) {
    if (event.action === TableActionEnum.Click) {
      this.selectedRezervacija = event.document;
      this._selectedRezervacije = [...event.selectedRows];
    }
  }

  updateEmisija(updateObject: any) {
    const emisija = updateObject.emisija;

    this.selectedEmisija.ProgramId = emisija.ProgramId;
    this.selectedEmisija.ProgramNaziv = emisija.ProgramNaziv;
    this.selectedEmisija.OsnovnaCenaCPP = emisija.OsnovnaCenaCPP;
    this.selectedEmisija.OsnovnaCenaSek = emisija.OsnovnaCenaSek;
    this.selectedEmisija.VremeEmitovanja = emisija.VremeEmitovanja;

    if (updateObject.isProgramChanged || updateObject.isVremeChanged) {
      this.osveziMontazuPoslePromeneEmisije.emit(updateObject);
    }
  }

  fetchEmisijaTableSettings() {
    this._subscriptions.push(
      this._backendService
        .fetchSettings(
          FormCode.MSMON_EMI,
          FormName.Montaza,
          EndpointTypes.AllEntities
        )
        .subscribe((response: any) => {
          response.Settings.formSettings.table =
            response.Settings.formSettings.lookupTable;
          const emisijeTableSettings = new TableSettings(
            response.Settings,
            'table'
          );

          this.emisijeTableSettingsSource.next(emisijeTableSettings);
          this.emisijeSource.next([]);
        })
    );
  }

  reloadEmisije() {
    if (this.targetProgram && this.targetDatum) {
      this.fetchEmisije();
    }
  }

  onTargetDateChange(event) {
    this.targetEmisija = null;
    this.targetDatum = moment(event.value.start).format('YYYY-MM-DD');

    if (this.targetProgram && this.targetDatum) {
      this.fetchEmisije();
    }
  }

  onTargetProgramChange(event) {
    this.targetEmisija = null;
    this.targetProgram = this.programi.find(
      program => program.Id === event.value
    );

    if (this.targetProgram && this.targetDatum) {
      this.fetchEmisije();
    }
  }

  fetchEmisije() {
    if (!this.targetProgram || !this.targetDatum) {
      this._notificationService.notyError(
        'Potrebno je prvo izabrati program i datum.'
      );

      return;
    }

    const filter = `?ProgramId=${this.targetProgram.Id}&DatumEmitovanja=${this.targetDatum}`;

    const payload = this._backendService.getRequestPayload(
      FormCode.MSEMI_EMRAUR,
      FormName.Montaza,
      EndpointTypes.GetAllEntities,
      filter,
      null,
      null,
      true
    );

    this._subscriptions.push(
      this._backendService
        .fetchMediaPlanSettings(payload)
        .subscribe((response: any) => {
          if (this._montazaService.hasError(response)) {
            this._notificationService.notyError(
              response['Errors'][0]['ErrorMessage']
            );

            return;
          }

          this.emisije = response.ServiceData;
          this.emisijeSource.next(this.emisije);
        })
    );
  }

  kopiraj(all: boolean = false) {
    switch (this.akcijaType) {
      case UredjivanjeActionTypeEnum.Emisije:
        this._modalService.openModal(
          'Da li želite da kopirate izabranu emisiju?',
          res => {
            if (res) {
              this.kopirajEmisiju();
            }
          }
        );

        break;
      case UredjivanjeActionTypeEnum.Rezervacije:
        this._modalService.openModal(
          'Da li želite da kopirate izabrane rezervacije?',
          res => {
            if (res) {
              this.kopirajRezervacije(all);
            }
          }
        );

        break;
      default:
        break;
    }
  }

  kopirajEmisiju() {
    if (!this.validateActions()) {
      this._notificationService.notyError('Niste uneli sve potrebne podatke.');

      return;
    }

    this._spinner.show();

    const entity = this.getActionsEntity(true, true);

    const payload = this._backendService.getRequestPayload(
      FormCode.MSMON_CER,
      FormName.Montaza,
      EndpointTypes.PutEntity,
      '',
      entity,
      null
    );

    this._subscriptions.push(
      this._backendService
        .fetchMediaPlanSettings(payload)
        .subscribe((response: any) => {
          if (this._montazaService.hasError(response)) {
            this._spinner.hide();
            this._notificationService.notyError(
              response['Errors'][0]['ErrorMessage']
            );

            return;
          }

          this.reloadEmisije(); // Ukoliko bude problem kod kopiranja emisija obrisati ovu liniju!
          this._spinner.hide();

          this._notificationService.openNoty(
            `Emisija uspešno kopirana na ${this.targetProgram.Naziv} i ${this.targetDatum}`
          );
        })
    );
  }

  kopirajRezervacije(all: boolean = false) {
    if (!this.validateActions(all)) {
      this._notificationService.notyError('Niste uneli sve potrebne podatke.');

      return;
    }

    this._spinner.show();

    const entity = this.getActionsEntity(all, true);
    const payload = this._backendService.getRequestPayload(
      FormCode.MSMON_CREZ,
      FormName.Montaza,
      EndpointTypes.PutEntity,
      '',
      entity,
      null
    );

    this._subscriptions.push(
      this._backendService
        .fetchMediaPlanSettings(payload)
        .subscribe((response: any) => {
          if (this._montazaService.hasError(response)) {
            this._spinner.hide();
            this._notificationService.notyError(
              response['Errors'][0]['ErrorMessage']
            );

            return;
          }

          this.fetchMontaza.next(true);
          this.reloadEmisije();
          this._spinner.hide();

          this._notificationService.openNoty(
            `Rezervacije uspešno kopirane na ${this.targetProgram.Naziv}, ${this.targetDatum} i ${this.targetEmisija.EmisijaNaziv}`
          );
        })
    );
  }

  premesti(all: boolean = false) {
    switch (this.akcijaType) {
      case UredjivanjeActionTypeEnum.Emisije:
        this._modalService.openModal(
          'Da li želite da premestite izabranu emisiju?',
          res => {
            if (res) {
              this.premestiEmisiju();
            }
          }
        );

        break;
      case UredjivanjeActionTypeEnum.Rezervacije:
        this._modalService.openModal(
          'Da li želite da premestite izabrane rezervacije?',
          res => {
            if (res) {
              this.premestiRezervacije(all);
            }
          }
        );

        break;

      default:
        break;
    }
  }

  premestiEmisiju() {
    if (!this.validateActions()) {
      this._notificationService.notyError('Niste uneli sve potrebne podatke.');

      return;
    }

    this._spinner.show();

    const entity = this.getActionsEntity(true, false);
    const payload = this._backendService.getRequestPayload(
      FormCode.MSMON_MER,
      FormName.Montaza,
      EndpointTypes.PutEntity,
      '',
      entity,
      null
    );

    this._subscriptions.push(
      this._backendService
        .fetchMediaPlanSettings(payload)
        .subscribe((response: any) => {
          if (this._montazaService.hasError(response)) {
            this._spinner.hide();
            this._notificationService.notyError(
              response['Errors'][0]['ErrorMessage']
            );

            return;
          }

          this.reloadEmisije(); // Ukoliko bude problem kod premestanja emisija obrisati ovu liniju!
          this._spinner.hide();

          this._notificationService.openNoty(
            `Emisija uspešno premeštena na ${this.targetProgram.Naziv} i ${this.targetDatum}`
          );

          this.zapocetoUredjivanje = false;
          this.selectedEmisija = null;
          this._montazaService.premestiEmisijuSubject.next(
            response.ServiceData.EmisijeUMontazi
          );
        })
    );
  }

  premestiRezervacije(all: boolean = false) {
    if (!this.validateActions(all)) {
      this._notificationService.notyError(
        'Niste izabrali sve potrebne podatke.'
      );

      return;
    }

    this._spinner.show();

    const entity = this.getActionsEntity(all);
    const payload = this._backendService.getRequestPayload(
      FormCode.MSMON_MREZ,
      FormName.Montaza,
      EndpointTypes.PutEntity,
      '',
      entity,
      null
    );

    this._subscriptions.push(
      this._backendService
        .fetchMediaPlanSettings(payload)
        .subscribe((response: any) => {
          if (this._montazaService.hasError(response)) {
            this._notificationService.notyError(
              response['Errors'][0]['ErrorMessage']
            );
            this._spinner.hide();

            return;
          }

          this.fetchMontaza.next(true);

          const preostaliSpotovi = response.ServiceData.Montaza.RezervacijeNaMontazi.filter(
            s =>
              !s.JeSpica &&
              this.selectedEmisija.EmisijaRasporedId === s.EmisijaRasporedId
          );

          let count = 1;
          preostaliSpotovi.forEach(s => {
            s.UiRbr = count++;
            s.Sifra = s.SpotSifra;
            s.Naziv = s.SpotNaziv;
            s.TrajanjeSekunde = s.SpotTrajanje;
            s.Spica = s.JeSpica;
            s.RezervacijaId = s.Id;
            s.KlijentId = s.KupacId;
            s.KlijentNaziv = s.KupacNaziv;
          });

          this.selectedEmisija.Spotovi = preostaliSpotovi;
          this.rezervacijeSource.next(preostaliSpotovi);

          this._montazaService.updateBlokovi(
            response.ServiceData.Montaza.Blokovi,
            this.selectedEmisija.Id
          );
          this.reloadEmisije();
          this._spinner.hide();
          this._notificationService.openNoty(
            `Rezervacije uspešno premeštene na ${this.targetProgram.Naziv}, ${this.targetDatum} i ${this.targetEmisija.EmisijaNaziv}`
          );
        })
    );
  }

  obrisi(all?: boolean) {
    switch (this.akcijaType) {
      case UredjivanjeActionTypeEnum.Rezervacije:
        const validation: boolean = all
          ? this.getAllRezervacije().length
          : this.validateSelectedRezervacije();
        if (!validation) {
          this._notificationService.notyError(
            all
              ? 'Ne postoje rezervacije za brisanje.'
              : 'Niste izabrali rezervacije za brisanje.'
          );

          return;
        }

        this._modalService.openModal(
          all
            ? 'Da li želite da obrišete sve rezervacije? Rezarvacije će biti obrisane i na media planu.'
            : 'Da li želite da obrišete izabrane rezervacije? Rezarvacije će biti obrisane i na media planu.',
          res => {
            if (res) {
              this.obrisiRezervaciju(all);
            }
          }
        );

        break;
      default:
        break;
    }
  }

  obrisiRezervaciju(all?: boolean) {
    this._spinner.show();
    const montaza = this._montazaService.montaza;

    const sveRezervacije = all
      ? this.getAllRezervacije()
      : this._selectedRezervacije;
    this.mapElemementBloka(sveRezervacije, montaza);
    const entity = sveRezervacije.map(r => {
      return {
        MontazaId: this._montazaService.montaza.Id,
        BlokId: r.elementBloka.BlokId,
        EmisijaRasporedId: r.elementBloka.EmisijaRasporedId,
        SadrzajId: r.elementBloka.SadrzajId,
        RezervacijaId: r.RezervacijaId,
        KlijentId: r.KlijentId,
        UgovorId: r.UgovorId,
        PodugovorId: r.PodugovorId,
        EmisijaId: r.EmisijaId,
        EmisijaEmisijaRasporedId: r.EmisijaRasporedId,
        TrajanjeSekunde: r.elementBloka.Sadrzaj.TrajanjeSekunde,
      };
    });

    const payload = this._backendService.getRequestPayload(
      FormCode.MSMON_DBREZ,
      FormName.Montaza,
      EndpointTypes.PutEntity,
      '',
      entity,
      null,
      true
    );

    this._subscriptions.push(
      this._backendService
        .fetchMediaPlanSettings(payload)
        .subscribe((response: any) => {
          if (this._montazaService.hasError(response)) {
            this._notificationService.notyError(
              response['Errors'][0]['ErrorMessage']
            );

            return;
          }

          const rezervacije = this.getAllRezervacije();
          const selectedRezervacijeIds = this._selectedRezervacije.map(
            r => r.RezervacijaId
          );
          this.selectedEmisija.Spotovi = all
            ? []
            : rezervacije.filter(
                r => !selectedRezervacijeIds.includes(r.RezervacijaId)
              );

          let count = 1;
          this.selectedEmisija.Spotovi.forEach(s => (s.UiRbr = count++));
          if (!this.selectedEmisija.Spotovi.length) {
            this.sacuvaj.next();
          } else {
            this.rezervacijeSource.next(this.selectedEmisija.Spotovi);
            this.selectedRezervacija = null;
            this.fetchMontaza.next();
          }
          this.reloadEmisije();
          this._spinner.hide();
          this._notificationService.openNoty('Uspešno brisanje rezervacije.');
        })
    );
  }

  mapElemementBloka(rezervacije, montaza) {
    rezervacije.forEach(r => {
      let elementBloka;

      montaza.Blokovi.find(b => {
        elementBloka = b.ElementiBloka.find(
          eb => eb.Sadrzaj.RezervacijaId === r.RezervacijaId
        );

        return elementBloka;
      });

      r.elementBloka = elementBloka;
    });
  }

  validateActions(all: boolean = false) {
    let validate =
      this.targetDatum && this.targetProgram && this.selectedEmisija;

    if (this.actionTypeRezervacije) {
      validate = this.validateRezervacije(validate, all);
    }

    return validate;
  }

  validateRezervacije(validate: boolean, all: boolean = false) {
    if (!all) {
      validate =
        validate &&
        this._selectedRezervacije &&
        !!this._selectedRezervacije.length;
    } else {
      validate = validate && !!this.getAllRezervacije().length;
    }

    return validate && this.targetEmisija;
  }

  validateSelectedRezervacije(): boolean {
    return !!(this._selectedRezervacije && this._selectedRezervacije.length);
  }

  getActionsEntity(all?: boolean, kopiranje?: boolean) {
    const entity: any = {
      TargetProgramId: this.targetProgram.Id,
      TargetProgramNaziv: this.targetProgram.Naziv,
      OsnovnaCenaSek: this.selectedEmisija.OsnovnaCenaSek,
      OsnovnaCenaCPP: this.selectedEmisija.OsnovnaCenaCPP,
      ProgramId: this._montazaService.programId,
      VremeEmitovanja: this.selectedEmisija.VremeEmitovanja,
      MontazaEmisijaRasporedId: this.selectedEmisija.Id,
      EmisijaNaziv: this.selectedEmisija.EmisijaNaziv,
      EmisijaId: this.selectedEmisija.EmisijaId,
      EmisijaSifra: this.selectedEmisija.EmisijaSifra,
    };

    // Rezervacije
    entity.TargetDatumEmitovanja = this.targetDatum;
    entity.Sadrzaj = all
      ? this.getAllRezervacije(kopiranje)
      : this._selectedRezervacije;
    entity.TargetDatumMontaze = this.targetDatum;
    entity.OriginMontazaId = this._montazaService.montaza.Id;
    entity.DatumMontaze = this._montazaService.datum;
    entity.OriginEmisijaRasporedId = this.selectedEmisija.Id;
    entity.OriginEmisijaRasporedIdGuid = this.selectedEmisija.EmisijaRasporedId;
    entity.OriginEmisijaIdGuid = this.selectedEmisija.EmisijaId;
    entity.OriginProgramId = this.selectedEmisija.ProgramId;
    entity.OriginDatumEmitovanja = this.selectedEmisija.DatumEmitovanja;

    if (this.actionTypeRezervacije) {
      entity.TargetEmisijaNaziv = this.targetEmisija.EmisijaNaziv;
      entity.TargetEmisijaId = this.targetEmisija.EmisijaId;
      entity.TargetEmisijaRasporedId = this.targetEmisija.Id;
      entity.TargetEmisijaSifra = this.targetEmisija.EmisijaSifra;
      entity.TargetVremeEmitovanja = this.targetEmisija.VremeEmitovanja;
      entity.TargetTrajanje = this.targetEmisija.Trajanje;
      entity.TargetOsnovnaCenaSek = this.targetEmisija.OsnovnaCenaSek;
      entity.TargetOsnovnaCenaCPP = this.targetEmisija.OsnovnaCenaCPP;
    } else {
      // Emisije
      // entity.Rezervacije = this.getAllRezervacije(kopiranje);
      // entity.TargetDatumEmitovanja = this.targetDatum;
      // entity.MontazaId = this._montazaService.montaza.Id;
      // entity.DatumEmitovanja = this._montazaService.datum;
      entity.TargetEmisijaNaziv = this.selectedEmisija.EmisijaNaziv;
      entity.TargetEmisijaId = this.selectedEmisija.EmisijaId;
      entity.TargetEmisijaSifra = this.selectedEmisija.EmisijaSifra;
      entity.TargetEmisijaRasporedId = this.selectedEmisija.EmisijaRasporedId;
      entity.TargetVremeEmitovanja = this.selectedEmisija.VremeEmitovanja;
      entity.TargetTrajanje = this.selectedEmisija.Trajanje;
      entity.TargetOsnovnaCenaSek = this.selectedEmisija.OsnovnaCenaSek;
      entity.TargetOsnovnaCenaCPP = this.selectedEmisija.OsnovnaCenaCPP;
    }

    return entity;
  }

  emisijeTableAction(event: any) {
    if (event.action === TableActionEnum.Select) {
      this.targetEmisija = event.document;
    }
  }

  onDestinacijaFilter(event: any) {
    this.destinacijaFilter = event.value;
  }

  filter() {
    const filterEmisije = this.emisije.filter(
      e =>
        e.EmisijaNaziv.toLowerCase().search(
          this.destinacijaFilter.toLocaleLowerCase()
        ) > -1
    );
    this.emisijeSource.next(filterEmisije);
  }
}



// WEBPACK FOOTER //
// ./src/app/components/montaza/uredjivanje-details/uredjivanje-details.component.ts